// POST /admin/dataengine/reconcile/floorplan_unit/split?id={id}
//
// Replaces one floorplan_unit row with N children. Conservation of
// total_area_sqm is enforced server-side (tolerance 0.5 m²) — the
// dialog mirrors the check locally so the operator can see the gap
// before submit. Every other column is inherited from the parent;
// only unit_type + total_area_sqm vary across children.

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Modal, inputBase } from "@/components/ui-prims";
import { ghostBtn, primaryBtn, smallBtn } from "@/components/admin-ui";
import { ReasonField } from "./ReasonField";

// Mirror of constants.FloorplanSplitAreaToleranceSqm — keep these in
// step. Server rejects on exceedance; FE warns earlier so the operator
// doesn't have to round-trip to learn the gap.
const TOLERANCE_SQM = 0.5;

// High-frequency split reasons. Curated from operator behaviour on
// the reconcile queue and the PROP-65 review thread: most splits are
// driven by extractor mis-merging or floor-vs-floor variation. The
// dialog still accepts free text via "Other…" so unusual cases aren't
// blocked.
const SPLIT_REASON_PRESETS: string[] = [
  "Stacked plan — separate unit_types per floor",
  "Brochure shows distinct unit_types in one row",
  "Floor variants (1st storey vs 2nd storey)",
  "Extractor merged different layouts into one row",
];

export type SplitChild = { unit_type: string; total_area_sqm: number };
export type SplitBody = { splits: SplitChild[]; reason: string };

interface Props {
  open: boolean;
  unit: { id: number; unit_type: string; total_area_sqm?: number | null } | null;
  busy: boolean;
  onSubmit: (body: SplitBody) => void;
  onCancel: () => void;
}

type Draft = { unit_type: string; area: string };

export function SplitUnitDialog({ open, unit, busy, onSubmit, onCancel }: Props) {
  return (
    <Modal
      open={open && unit != null}
      onClose={busy ? () => {} : onCancel}
      title={unit ? `Split unit · ${unit.unit_type || "—"}` : "Split unit"}
      width={520}
    >
      {unit && (
        <SplitForm
          key={unit.id}
          unit={unit}
          busy={busy}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      )}
    </Modal>
  );
}

function SplitForm({ unit, busy, onSubmit, onCancel }: { unit: NonNullable<Props["unit"]>; busy: boolean; onSubmit: Props["onSubmit"]; onCancel: () => void }) {
  // Default to a 2-way even split if the parent has an area; otherwise
  // leave areas blank so the operator types them in.
  const parentArea = unit.total_area_sqm ?? null;
  const initial: Draft[] = useMemo(() => {
    const even = parentArea != null ? round2(parentArea / 2) : null;
    return [
      { unit_type: `${unit.unit_type}-1`, area: even == null ? "" : String(even) },
      { unit_type: `${unit.unit_type}-2`, area: even == null ? "" : String(even) },
    ];
  }, [unit.unit_type, parentArea]);

  const [drafts, setDrafts] = useState<Draft[]>(initial);
  const [reason, setReason] = useState("");

  useEffect(() => { setDrafts(initial); setReason(""); }, [initial]);

  function addRow() {
    if (drafts.length >= 16) return;
    setDrafts((ds) => [...ds, { unit_type: "", area: "" }]);
  }
  function removeRow(i: number) {
    if (drafts.length <= 2) return;
    setDrafts((ds) => ds.filter((_, idx) => idx !== i));
  }
  function patchRow(i: number, p: Partial<Draft>) {
    setDrafts((ds) => ds.map((d, idx) => idx === i ? { ...d, ...p } : d));
  }

  const sum = drafts.reduce((s, d) => {
    const v = parseFloat(d.area);
    return Number.isFinite(v) ? s + v : s;
  }, 0);
  const gap = parentArea == null ? null : sum - parentArea;
  const gapOk = parentArea == null ? false : Math.abs(gap!) <= TOLERANCE_SQM;
  const allLabelsFilled = drafts.every((d) => d.unit_type.trim() !== "");
  const labelsUnique = new Set(drafts.map((d) => d.unit_type.trim())).size === drafts.length;
  const allAreasPositive = drafts.every((d) => {
    const v = parseFloat(d.area);
    return Number.isFinite(v) && v > 0;
  });
  const reasonOk = reason.trim().length > 0;

  const submittable = !busy && parentArea != null && gapOk && allLabelsFilled && labelsUnique && allAreasPositive && reasonOk;

  function submit() {
    if (!submittable) return;
    onSubmit({
      splits: drafts.map((d) => ({
        unit_type:      d.unit_type.trim(),
        total_area_sqm: parseFloat(d.area),
      })),
      reason: reason.trim(),
    });
  }

  return (
    <>
      <div style={infoLine}>
        Parent area: <strong>{parentArea == null ? "—" : `${parentArea} m²`}</strong>
        {" · "}
        Children must sum within ±{TOLERANCE_SQM} m². <em>Rooms/geometry are copied to every child</em> — re-attribute via the bbox editor afterwards.
      </div>

      {parentArea == null && (
        <div style={warnLine}>
          This unit has no <code>total_area_sqm</code>. Patch the area first, then split.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 12 }}>
        {drafts.map((d, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 110px 32px", gap: 8, alignItems: "center" }}>
            <input
              style={inputBase}
              value={d.unit_type}
              onChange={(e) => patchRow(i, { unit_type: e.target.value })}
              placeholder={`Child ${i + 1} unit_type`}
              disabled={busy}
            />
            <input
              style={inputBase}
              type="number"
              min={0}
              step={0.1}
              value={d.area}
              onChange={(e) => patchRow(i, { area: e.target.value })}
              placeholder="m²"
              disabled={busy}
            />
            <button
              type="button"
              style={{ ...smallBtn, opacity: drafts.length > 2 ? 1 : 0.4, cursor: drafts.length > 2 ? "pointer" : "not-allowed" }}
              onClick={() => removeRow(i)}
              disabled={busy || drafts.length <= 2}
              title="Remove"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button type="button" style={smallBtn} onClick={addRow} disabled={busy || drafts.length >= 16}>+ add child</button>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: gapOk ? "var(--ink-3)" : "var(--neg)" }}>
          Σ {round2(sum)} m² · gap {parentArea == null ? "—" : `${(gap! >= 0 ? "+" : "")}${round2(gap!)} m²`}
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={fieldLabel}>Reason *</span>
          <ReasonField
            value={reason}
            onChange={setReason}
            presets={SPLIT_REASON_PRESETS}
            placeholder="e.g. brochure shows two distinct unit_types — splitting"
            disabled={busy}
          />
        </label>
      </div>

      <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" style={ghostBtn} onClick={onCancel} disabled={busy}>Cancel</button>
        <button
          type="button"
          style={{ ...primaryBtn, opacity: submittable ? 1 : 0.5, cursor: submittable ? "pointer" : "not-allowed" }}
          onClick={submit}
          disabled={!submittable}
        >
          {busy ? "Splitting…" : `Split into ${drafts.length}`}
        </button>
      </div>
    </>
  );
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

const fieldLabel: CSSProperties = {
  fontFamily: "var(--font-ui)", fontSize: 10.5, fontWeight: 600,
  letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ink-4)",
};
const infoLine: CSSProperties = {
  fontSize: 12, color: "var(--ink-3)", lineHeight: 1.55,
};
const warnLine: CSSProperties = {
  marginTop: 10, padding: "8px 10px", borderRadius: 6,
  background: "rgba(138,90,0,0.10)", color: "#5a3e00", fontSize: 12,
};
